import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../lib/firebase";
import { useAuth } from "../context/AuthContext";
import { useIsPhone } from "../hooks/useIsPhone";
import { useSnackbar } from "../context/SnackbarContext";

// ADR-136: dashboard onboarding split is now
//   persons/{personId}        - name parts (displayName denormed by CF)
//   personProfiles/{personId} - private tier (title, organization, bio)
//   authAccounts/{uid}        - profileReviewed onboarding flag
// uid -> personId resolution comes via authAccounts/{uid}.personId.

interface ProfileData {
  firstName: string;
  lastName: string;
  honorificPrefix: string;
  honorificSuffix: string;
  title: string;
  organization: string;
  bio: string;
}

export function AccountSetupPage() {
  const isPhone = useIsPhone();
  const { user, signOut, markSetupComplete } = useAuth();
  const { showError } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [personId, setPersonId] = useState<string | null>(null);
  const [profile, setProfile] = useState<ProfileData>({
    firstName: "",
    lastName: "",
    honorificPrefix: "",
    honorificSuffix: "",
    title: "",
    organization: "",
    bio: "",
  });

  useEffect(() => {
    if (!user) return;
    async function loadProfile() {
      const authAccountDoc = await getDoc(doc(db, "authAccounts", user!.uid));
      const resolvedPersonId = authAccountDoc.exists()
        ? (authAccountDoc.data().personId as string | undefined) ?? null
        : null;
      setPersonId(resolvedPersonId);

      if (resolvedPersonId) {
        const personDoc = await getDoc(doc(db, "persons", resolvedPersonId));
        if (personDoc.exists()) {
          const data = personDoc.data();
          setProfile({
            firstName: data.firstName ?? "",
            lastName: data.lastName ?? "",
            honorificPrefix: data.honorificPrefix ?? "",
            honorificSuffix: data.honorificSuffix ?? "",
            title: "",
            organization: "",
            bio: "",
          });
        }
        const profileDoc = await getDoc(
          doc(db, "personProfiles", resolvedPersonId),
        );
        if (profileDoc.exists()) {
          const pData = profileDoc.data();
          setProfile((prev) => ({
            ...prev,
            title: pData.title ?? "",
            organization: pData.organization ?? "",
            bio: pData.bio ?? "",
          }));
        }
      }
      setLoading(false);
    }
    loadProfile();
  }, [user]);

  async function handleSave() {
    if (!user) return;
    if (!personId) {
      // The bootstrap path (AuthContext.resolveAuthState -> activateAccount)
      // should have minted a persons row + authAccounts pointer before we
      // landed here. Reaching this branch means activateAccount returned
      // matched:false despite a valid role claim, or the authAccounts read
      // failed mid-load. Surface it loudly so the user has a recoverable
      // action instead of a silent dead-end.
      showError(
        "Account setup failed — your profile record is missing. Sign out and back in to retry, or contact support.",
      );
      return;
    }
    setSaving(true);
    try {
      // displayName is denormed by the onPersonUpdated CF; client never
      // writes it to persons. Computed locally only to sync Firebase Auth's
      // own profile (which is uid-keyed and outside Firestore).
      const displayName = [
        profile.honorificPrefix,
        profile.firstName,
        profile.lastName,
        profile.honorificSuffix,
      ]
        .filter(Boolean)
        .join(" ");

      // setDoc({ merge: true }) instead of updateDoc — the persons /
      // personProfiles doc may not exist yet for organizers/admins seeded
      // directly via setUserRole (no contact claim path on the dashboard).
      // Merging creates-or-updates so the form save works in both cases.
      await setDoc(
        doc(db, "persons", personId),
        {
          firstName: profile.firstName || null,
          lastName: profile.lastName || null,
          honorificPrefix: profile.honorificPrefix || null,
          honorificSuffix: profile.honorificSuffix || null,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      await setDoc(
        doc(db, "personProfiles", personId),
        {
          title: profile.title || null,
          organization: profile.organization || null,
          bio: profile.bio || null,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      await setDoc(
        doc(db, "authAccounts", user.uid),
        {
          profileReviewed: true,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );

      // Update Auth display name
      const { updateProfile } = await import("firebase/auth");
      await updateProfile(auth.currentUser!, { displayName });

      markSetupComplete();
    } catch (err) {
      console.error("Failed to save profile:", err);
    } finally {
      setSaving(false);
    }
  }

  function handleSkip() {
    if (!user) return;
    setDoc(
      doc(db, "authAccounts", user.uid),
      {
        profileReviewed: true,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    )
      .then(() => markSetupComplete())
      .catch((err) => {
        console.error("Failed to skip setup:", err);
        showError("Couldn't save. Sign out and back in to retry.");
      });
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        p: isPhone ? 2 : 3,
      }}
    >
      <Card sx={{ maxWidth: 560, width: "100%" }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Welcome to the Admin Portal
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Review your profile information before getting started. You can
            update this later from the admin portal.
          </Typography>

          <Stack spacing={2}>
            <Stack direction={isPhone ? "column" : "row"} spacing={2}>
              <TextField
                label="First Name"
                value={profile.firstName}
                onChange={(e) =>
                  setProfile((p) => ({ ...p, firstName: e.target.value }))
                }
                fullWidth
                required
              />
              <TextField
                label="Last Name"
                value={profile.lastName}
                onChange={(e) =>
                  setProfile((p) => ({ ...p, lastName: e.target.value }))
                }
                fullWidth
                required
              />
            </Stack>
            <Stack direction={isPhone ? "column" : "row"} spacing={2}>
              <TextField
                label="Prefix (e.g. Dr., Prof.)"
                value={profile.honorificPrefix}
                onChange={(e) =>
                  setProfile((p) => ({
                    ...p,
                    honorificPrefix: e.target.value,
                  }))
                }
                fullWidth
              />
              <TextField
                label="Suffix (e.g. PhD, Jr.)"
                value={profile.honorificSuffix}
                onChange={(e) =>
                  setProfile((p) => ({
                    ...p,
                    honorificSuffix: e.target.value,
                  }))
                }
                fullWidth
              />
            </Stack>
            <TextField
              label="Title"
              value={profile.title}
              onChange={(e) =>
                setProfile((p) => ({ ...p, title: e.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Organization"
              value={profile.organization}
              onChange={(e) =>
                setProfile((p) => ({ ...p, organization: e.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Bio"
              value={profile.bio}
              onChange={(e) =>
                setProfile((p) => ({ ...p, bio: e.target.value }))
              }
              fullWidth
              multiline
              rows={3}
            />

            {/* Phone: Save full width on top, the two text actions under it. */}
            <Stack
              direction={isPhone ? "column-reverse" : "row"}
              spacing={isPhone ? 1 : 2}
              justifyContent="flex-end"
            >
              <Button variant="text" onClick={signOut} disabled={saving}>
                Sign Out
              </Button>
              <Button variant="text" onClick={handleSkip} disabled={saving}>
                Skip for Now
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || !profile.firstName || !profile.lastName}
              >
                {saving ? <CircularProgress size={20} /> : "Save & Continue"}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
